import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
	BringzzIcon,
	BringzzText,
	BringzzButton,
	BringzzAccordion,
	BringzzBottomDrawer,
	BringzzRotatingSelect
} from '@bringzz/components';
import { useOverlay } from 'context/OverlayContext';
import { useBasket } from 'context/BasketContext';
import { DELIVERY_TYPES, getDeliveryTypeLabel } from 'utils/constants';
import { useNavigation } from 'context/NavigationContext';
import { useAuth } from 'context/AuthContext';
import { useSocket } from 'context/SocketClusterContext';
import AddressSelect from '../AddressSelect';
import DefaultProductImage from 'assets/DefaultProductImage.png';

const MOCK_PAYMENT_METHODS = [
	{
		id: 1,
		type: 'credit_card',
		last4: '4242',
		brand: 'Visa',
		icon: 'IconCreditCard'
	},
	{
		id: 2,
		type: 'credit_card',
		last4: '0904',
		brand: 'Mastercard',
		icon: 'IconCreditCard'
	},
	{
		id: 3,
		type: 'sofort',
		brand: 'Sofort',
		icon: 'IconBank'
	},
	{
		id: 4,
		type: 'klarna',
		brand: 'Klarna',
		icon: 'IconBank'
	}
];

import { toast } from 'react-toastify';
const CheckoutSummary = ({ close, className, deliveryType = 0, pharmacy, pharmacies = [], onPharmacySelect, ...props }) => {
	const { t } = useTranslation();
	const [currentHours, setCurrentHours] = useState(null);
	const [currentMinutes, setCurrentMinutes] = useState(null);
	const [selectingTime, setSelectingTime] = useState(false);
	const [scheduledTime, setScheduledTime] = useState(null);
	const [selectedTip, setSelectedTip] = useState(null);
	const [customTipAmount, setCustomTipAmount] = useState('');
	const [showPaymentMethods, setShowPaymentMethods] = useState(false);
	const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(MOCK_PAYMENT_METHODS[0]);
	const [showPharmacySelect, setShowPharmacySelect] = useState(false);
	const [selectedPharmacy, setSelectedPharmacy] = useState(pharmacy);
	const [initialPharmacy] = useState(pharmacy);
	const [showAddressSelect, setShowAddressSelect] = useState(false);
	const [userAddress, setUserAddress] = useState(null);
	const { showOverlayContent, hideOverlay } = useOverlay();
	const { basketItems, getBasketTotal, getBasketSubTotal, getFees, setBasketTip, getBasketTip } = useBasket();
	const { navigate } = useNavigation();
	const { user, updateUserAddresses } = useAuth();
	const { isConnected } = useSocket();

	const getFormattedAddress = (address) => {
		if (!address) return t('component.basket.address.select');
		const streetPart = [address.streetName, address.houseNumber].filter(Boolean).join(' ');
		const cityPart = [address.postalCode, address.cityName].filter(Boolean).join(' ');
		return [streetPart, cityPart].filter(Boolean).join(', ');
	};

	useEffect(() => {
		if (user?.activeAddress) {
			setUserAddress(getFormattedAddress(user.activeAddress));
		} else {
			setUserAddress(t('component.basket.address.select'));
		}
	}, [user?.activeAddress, t]);

	const handlePlaceOrder = () => {
		if (!isConnected) {
			toast.error(t('component.checkout_summary.connection_error', 'Connection error. Please check your internet and try again.'));
			return;
		}
		close();
		navigate('/payment', {
			state: {
				deliveryType,
				pharmacy: selectedPharmacy,
				tip: getBasketTip()
			}
		});
	};

	const handlePharmacySelection = (pharmacyItem) => {
		setSelectedPharmacy(pharmacyItem);
		if (onPharmacySelect) {
			onPharmacySelect(pharmacyItem);
		}
		setShowPharmacySelect(false);
	};

	const hoursOptions = [
		{ label: '1', value: 1 },
		{ label: '2', value: 2 },
		{ label: '3', value: 3 },
		{ label: '4', value: 4 },
		{ label: '5', value: 5 },
		{ label: '6', value: 6 },
		{ label: '7', value: 7 },
		{ label: '8', value: 8 },
		{ label: '9', value: 9 },
		{ label: '10', value: 10 },
		{ label: '11', value: 11 },
		{ label: '12', value: 12 }
	];

	const generateMinutesOptions = () => {
		let array = [];
		for (let index = 0; index < 60; index++) {
			array.push({ value: index, label: index.toString().padStart(2, '0') });
		}
		return array;
	};
	const minutesOptions = generateMinutesOptions();
	const closeTimeSelection = () => {
		const time = `${currentHours}:${currentMinutes.toString().padStart(2, '0')}`;
		setScheduledTime(time);
		setSelectingTime(false);
	};
	const changeHours = result => {
		setCurrentHours(result.value);
	};
	const changeMinutes = result => {
		setCurrentMinutes(result.value);
	};
	const selectTip = amount => {
		// Toggle: if clicking the same tip, deselect it
		if (selectedTip === amount) {
			setSelectedTip(null);
			setCustomTipAmount('');
			setBasketTip(0);
		} else {
			setSelectedTip(amount);
			// Clear custom tip amount when selecting predefined amounts
			if (amount !== 'Other') {
				setCustomTipAmount('');
				// Save tip to basket context
				setBasketTip(amount || 0);
			}
		}
	};

	const handleCustomTipChange = (e) => {
		const value = e.target.value;
		// Only allow numbers and decimal point
		if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
			setCustomTipAmount(value);
			// Update basket tip with custom amount
			setBasketTip(value || 0);
		}
	};

	const handlePaymentMethodSelect = (method) => {
		setSelectedPaymentMethod(method);
		setShowPaymentMethods(false);
	};

	return (
		<div className=''>
			{!selectingTime ? (
				<div className='px-4 pb-4'>
					<div className={`flex items-center py-4 px-2 ${pharmacies.length > 1 ? 'cursor-pointer' : ''}`} onClick={() => pharmacies.length > 1 ? setShowPharmacySelect(true) : null}>
						<div className='w-10 flex justify-center'>
							<BringzzIcon
								size='24'
								icon='IconPharmacy'
								folder={'HealthIcons'}
							/>
						</div>
						<div className='flex-1 mx-4'>
							<BringzzText tag='h3'>{t('component.checkout_summary.pharmacy')}</BringzzText>
							<BringzzText className='text-sm text-black/50'>
								{selectedPharmacy.pharmacy_name || t('component.checkout_summary.pharmacy_name')}
								{pharmacies.length === 1 ? (
										<span className='ml-2 p-2 rounded-lg font-bold text-xs bg-yellow-400 text-midnight-blue'>{t('component.checkout_summary.only_fit')}</span>
									) : (
									selectedPharmacy.pharmacy_id === initialPharmacy.pharmacy_id && (
										<span className='ml-2 p-2 rounded-lg font-bold text-xs bg-indigo-100 text-blue-500'>{t('component.checkout_summary.best_fit')}</span>
									)
								)}
							</BringzzText>
						</div>
						<div className='w-8'>
							{pharmacies.length > 1 && (
								<BringzzIcon size='18' icon={'IconPencil'} folder={'LineIcons'} />
							)}
						</div>
					</div>
					<div className='flex items-center py-4 px-2 cursor-pointer' onClick={() => setShowAddressSelect(true)}>
						<div className='w-10 flex justify-center'>
							<BringzzIcon
								size='24'
								icon='IconMapMarker'
								folder={'LineIcons'}
							/>
						</div>
						<div className='flex-1 mx-4'>
							<BringzzText tag='h3'>Address</BringzzText>
							<BringzzText className='text-sm text-black/50'>
								{userAddress || t('component.basket.address.select')}
							</BringzzText>
						</div>
						<div className='w-8'>
							<BringzzIcon size='18' icon={'IconPencil'} folder={'LineIcons'} />
						</div>
					</div>
					<div className='flex items-center py-4 px-2 cursor-pointer' onClick={close}>
						<div className='w-10 flex justify-center'>
							<BringzzIcon
								size='24'
								icon='IconPackage'
								folder={'LineIcons'}
							/>
						</div>
						<div className='flex-1 mx-4'>
							<BringzzText tag='h3'>Delivery type</BringzzText>
							<BringzzText className='text-sm text-black/50'>
								{getDeliveryTypeLabel(deliveryType, t)}
							</BringzzText>
						</div>
						<div className='w-8'>
							<BringzzIcon size='18' icon={'IconPencil'} folder={'LineIcons'} />
						</div>
					</div>
					{deliveryType === DELIVERY_TYPES.BRINGZZ_PICKUP || deliveryType === DELIVERY_TYPES.MAIL_DELIVERY && <div className='flex items-center py-4 px-2 cursor-pointer'>
						<div className='w-10 flex justify-center'>
							<BringzzIcon size='24' icon='IconMapMarker' folder={'LineIcons'} />
						</div>
						<div className='flex-1 mx-4'>
							<BringzzText tag='h3'>{t('component.checkout_summary.deliver_to')}</BringzzText>
							<BringzzText className='text-sm text-black/50'>
								{t('component.checkout_summary.home')}
							</BringzzText>
						</div>
						<div className='w-8'>
							<BringzzIcon size='18' icon={'IconPencil'} folder={'LineIcons'} />
						</div>
					</div>}
					{/* <div 
						className='flex items-center justify-between py-4 px-2 cursor-pointer'
						onClick={() => setShowPaymentMethods(true)}
					>
						<BringzzIcon size='18' icon='IconCreditCard' folder={'LineIcons'} />
						<div className='flex flex-col pr-8'>
							<BringzzText tag='h3'>{t('component.checkout_summary.payment_method')}</BringzzText>
							<div className='w-64'>
								<BringzzText
									tag='h3'
									className='text-normal text-black/50 whitespace-pre-line truncate'
								>
									{selectedPaymentMethod.brand} ***{selectedPaymentMethod.last4 || ''}
								</BringzzText>
							</div>
						</div>
						<BringzzIcon size='18' icon={'IconPencil'} folder={'LineIcons'} />
					</div> */}
					{deliveryType == DELIVERY_TYPES.BRINGZZ_PICKUP && (
						<div className='bg-gray-200 py-3 px-4 rounded-md'>
							<div className='flex items-center space-x-4 justify-between'>
								<BringzzText tag='h3'>{t('component.checkout_summary.add_tip')}</BringzzText>
								<div className='flex items-center gap-x-2'>
									<BringzzButton
										className={`text-olive-green rounded-lg !px-3 flex justify-center font-semibold ${selectedTip === 1 ? 'bg-magic-lilac' : 'bg-sand '}`}
										onClick={() => selectTip(1)}
									>
										€1
									</BringzzButton>
									<BringzzButton
										className={`text-olive-green rounded-lg !px-3 flex justify-center font-semibold ${selectedTip === 3 ? 'bg-magic-lilac ' : 'bg-sand '}`}
										onClick={() => selectTip(3)}
									>
										€3
									</BringzzButton>
									<BringzzButton
										className={`text-olive-green rounded-lg !px-3 flex justify-center font-semibold ${selectedTip === 5 ? 'bg-magic-lilac' : 'bg-sand '}`}
										onClick={() => selectTip(5)}
									>
										€5
									</BringzzButton>
									<BringzzButton
										className={`text-olive-green rounded-lg !px-3 flex justify-center font-semibold ${selectedTip === 'Other' ? 'bg-magic-lilac' : 'bg-sand '}`}
										onClick={e => {
											e.stopPropagation();
											selectTip('Other');
										}}
									>
										{t('component.checkout_summary.other')}
									</BringzzButton>
								</div>
							</div>
							{selectedTip === 'Other' && (
								<div className='mt-3 flex items-center gap-x-2'>
									<BringzzText className='text-sm'>{t('component.checkout_summary.custom_amount', 'Enter amount')}:</BringzzText>
									<div className='flex items-center bg-white rounded-lg px-3 py-1.5 border border-gray-300 focus-within:border-magic-lilac'>
										<span className='text-sm font-medium mr-1'>€</span>
										<input
											type='text'
											value={customTipAmount}
											onChange={handleCustomTipChange}
											placeholder='0.00'
											className='w-20 outline-none text-sm'
											autoFocus
										/>
									</div>
								</div>
							)}
						</div>
					)}
					{/* Order Summary Accordion */}
					<div className='py-4 px-2'>
						<BringzzAccordion
							defaultValue={false}
							title={
								<div className='flex items-center'>
									<div className='w-10 flex justify-center'>
										<BringzzIcon size='24' icon='IconTicketAlt' folder={'LineIcons'} />
									</div>
									<div className='flex-1 mx-4'>
										<BringzzText tag='h3'>{t('component.checkout_summary.order_summary')}</BringzzText>
										<BringzzText className='text-sm text-black/50'>
											{basketItems.length} {basketItems.length === 1 ? 'item' : 'items'} • {t('component.checkout_summary.total')}: €{getBasketTotal()}
										</BringzzText>
									</div>
								</div>
							}
						>
							<div className='space-y-3 mt-3'>
								{basketItems.map((item, index) => (
									<div key={item.productIntegrationId || index} className='flex items-start space-x-3 pb-3 border-b last:border-b-0'>
										{/* Product Image */}
										<img
											src={item.imgUrl || DefaultProductImage}
											alt={item.name}
											className='w-12 h-12 object-cover rounded'
										/>
										
										{/* Product Details */}
										<div className='flex-1'>
											<BringzzText className='font-medium text-sm'>
												{item.name}
											</BringzzText>
											<BringzzText className='text-xs text-gray-500'>
												{item.manufacturer}
											</BringzzText>
											<BringzzText className='text-xs text-gray-500'>
												{item.packaging} | {item.packageQuantity}
											</BringzzText>
										</div>
										
										{/* Quantity and Price */}
										<div className='text-right'>
											<BringzzText className='font-medium text-sm'>
												€ {(item.price * item.quantity).toFixed(2)}
											</BringzzText>
											<BringzzText className='text-xs text-gray-500'>
												Qty: {item.quantity}
											</BringzzText>
										</div>
									</div>
								))}
								
								{/* Summary */}
								<div className='pt-3 space-y-1'>
									<div className='flex justify-between'>
										<BringzzText className='text-sm'>{t('component.basket.summary.subtotal')}</BringzzText>
										<BringzzText className='text-sm'>€ {getBasketSubTotal()}</BringzzText>
									</div>
									<div className='flex justify-between'>
										<BringzzText className='text-sm'>{t('component.basket.summary.delivery_fee')}</BringzzText>
										<BringzzText className='text-sm'>€ {getFees()}</BringzzText>
									</div>
									{getBasketTip() > 0 && (
										<div className='flex justify-between'>
											<BringzzText className='text-sm'>{t('component.checkout_summary.tip', 'Tip')}</BringzzText>
											<BringzzText className='text-sm'>€ {parseFloat(getBasketTip()).toFixed(2)}</BringzzText>
										</div>
									)}
									<div className='flex justify-between font-semibold pt-2 border-t'>
										<BringzzText>{t('component.basket.summary.total')}</BringzzText>
										<BringzzText>€ {getBasketTotal()}</BringzzText>
									</div>
								</div>
							</div>
						</BringzzAccordion>
					</div>

					<div className='my-6 px-4'>
						<BringzzText tag='h5' className='font-regular text-center'>
							{t('component.checkout_summary.terms_agreement')}
						</BringzzText>
					</div>
					<div className='w-full flex justify-center gap-x-4'>
						<BringzzButton
							className='bg-magic-lilac rounded-full flex justify-center font-semibold'
							onClick={handlePlaceOrder}
						>
							{t('component.checkout_summary.place_order')} (€ {getBasketTotal()}) {scheduledTime ? `${t('component.checkout_summary.at')} ${scheduledTime}` : ''}
						</BringzzButton>
						{/* <BringzzIcon
							folder='LineIcons'
							className='bg-lemon-yellow text-midnight-blue p-2 rounded-full'
							size='20'
							icon='IconClockAlt1'
							onClick={() => setSelectingTime(true)}
						/> */}
					</div>
				</div>
			) : (
				<>
					<div className='flex items-center relative gap-x-4 justify-center'>
						<div className='h-12 absolute top-1/2 right-0 w-full border-y -translate-y-1/2'></div>
						<div>
							<BringzzRotatingSelect
								options={hoursOptions}
								onSelect={changeHours}
							/>
						</div>
						<div>
							<BringzzRotatingSelect
								options={minutesOptions}
								onSelect={changeMinutes}
							/>
						</div>
					</div>

					<div className='flex justify-center items-center py-6'>
						<BringzzButton
							size='lg'
							className='bg-magic-lilac '
							onClick={closeTimeSelection}
						>
							{t('component.checkout_summary.apply')}
						</BringzzButton>
					</div>
				</>
			)}

			{/* <BringzzBottomDrawer
				isOpen={showPaymentMethods}
				close={() => setShowPaymentMethods(false)}
				title={t('component.checkout_summary.select_payment_method')}
			>
				<div className="p-4 space-y-4">
					{MOCK_PAYMENT_METHODS.map((method) => (
						<div
							key={method.id}
							className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-gray-50"
							onClick={() => handlePaymentMethodSelect(method)}
						>
							<div className="flex items-center space-x-4">
								<BringzzIcon
									size='24'
									icon={method.icon}
									folder={'LineIcons'}
									className={method.type === selectedPaymentMethod.type ? 'text-magic-lilac' : ''}
								/>
								<div>
									<BringzzText tag="h3" className="font-medium">
										{method.brand}
									</BringzzText>
									{method.last4 && (
										<BringzzText className="text-sm text-gray-500">
											**** {method.last4}
										</BringzzText>
									)}
								</div>
							</div>
							{method.id === selectedPaymentMethod.id && (
								<BringzzIcon
									size='24'
									icon="IconCheck"
									folder={'LineIcons'}
									className="text-magic-lilac"
								/>
							)}
						</div>
					))}
				</div>
			</BringzzBottomDrawer> */}

			{/* Pharmacy Selection Drawer */}
			{pharmacies.length > 1 && (
				<BringzzBottomDrawer
					isOpen={showPharmacySelect}
					close={() => setShowPharmacySelect(false)}
					title={t('component.checkout_summary.select_pharmacy', 'Select Pharmacy')}
				>
					<div className="p-4 space-y-4">
						<BringzzText className="text-sm text-gray-500 mb-4">
							{pharmacies.length} pharmacies available
						</BringzzText>

						{pharmacies.map((pharmacyItem) => (
							<div
								key={pharmacyItem.pharmacy_id}
								className={`flex items-start justify-between p-4 border rounded-lg cursor-pointer ${
									selectedPharmacy.pharmacy_id === pharmacyItem.pharmacy_id 
										? 'bg-magic-lilac/10 border-magic-lilac' 
										: 'hover:bg-gray-50'
								}`}
								onClick={() => handlePharmacySelection(pharmacyItem)}
							>
								<div className="flex items-start space-x-4 flex-1">
									<BringzzIcon 
										size='24' 
										icon='IconPharmacy' 
										folder={'HealthIcons'}
										className="mt-1"
									/>
									<div className="flex-1">
										<div className="flex items-center space-x-2">
											<BringzzText tag="h3" className="font-medium">
												{pharmacyItem.pharmacy_name}
											</BringzzText>
											{pharmacyItem.pharmacy_id === initialPharmacy.pharmacy_id && (
												<span className='px-2 py-1 rounded-lg font-bold text-xs bg-indigo-100 text-blue-500'>
													{t('component.checkout_summary.best_fit')}
												</span>
											)}
										</div>
										<BringzzText className="text-sm text-gray-500">
											{pharmacyItem.street_name} {pharmacyItem.house_number}, {pharmacyItem.postal_code} {pharmacyItem.city_name}
										</BringzzText>
										<div className="flex items-center space-x-4 mt-2">
											<div className="flex items-center space-x-1">
												<BringzzIcon size='16' icon='IconMapMarker' folder={'LineIcons'} />
												<BringzzText className="text-xs text-gray-500">
													{Number(pharmacyItem.distance).toFixed(1) || "0.0"} km
												</BringzzText>
											</div>
											{pharmacyItem.telefon && (
												<div className="flex items-center space-x-1">
													<BringzzIcon size='16' icon='IconPhone' folder={'LineIcons'} />
													<BringzzText className="text-xs text-gray-500">
														{pharmacyItem.telefon}
													</BringzzText>
												</div>
											)}
										</div>
									</div>
								</div>
								{selectedPharmacy.pharmacy_id === pharmacyItem.pharmacy_id && (
									<BringzzIcon 
										size='24' 
										icon="IconCheck" 
										folder={'LineIcons'} 
										className="text-magic-lilac" 
									/>
								)}
							</div>
						))}
					</div>
				</BringzzBottomDrawer>
			)}

			{/* Address Selection Drawer */}
			<BringzzBottomDrawer
				isOpen={showAddressSelect}
				close={() => setShowAddressSelect(false)}
				title={t('component.basket.address.select_title', 'Select Address')}
			>
				<AddressSelect 
					close={() => setShowAddressSelect(false)}
					setAddress={setUserAddress}
					onAddressUpdated={async () => {
						await updateUserAddresses();
						setShowAddressSelect(false);
					}}
				/>
			</BringzzBottomDrawer>
		</div>
	);
};

export default CheckoutSummary;
